import React, { lazy, Suspense } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import { getModalParentRoute, isModalRoute, ROUTES } from "~/router";
import { Login } from "~/screens/Auth/Login/Login";
import { Signup } from "~/screens/Auth/Signup/Signup";
import { ChunkLoadErrorBoundary, ErrorBoundary, NotFound } from "~/sections";
import { ROLES, SOURCE } from "~/shared.types";
import { customIcons } from "~/ui/common/Icons";
import { Guest, HomeRedirect, ProtectedRoute } from "./components";
import { HubLayout } from "./layouts";
import { ModalRouter } from "./ModalRouter";

const { SpinnerIcon } = customIcons;

// Admin screens
const BannerManagementPage = lazy(() =>
  import("~/screens/Admins/banner-management/banner-management-page").then(
    (m) => ({ default: m.BannerManagementPage }),
  ),
);
const InsuranceProvidersPage = lazy(() =>
  import("~/screens/Admins/InsuranceProviders/insurance-providers-page").then(
    (m) => ({ default: m.InsuranceProvidersPage }),
  ),
);
const InsuranceProviderDetailPage = lazy(() =>
  import("~/screens/Admins/InsuranceProviders/insurance-provider-detail-page").then(
    (m) => ({ default: m.InsuranceProviderDetailPage }),
  ),
);
const UserEventsPage = lazy(() =>
  import("~/screens/Admins/UserEvents/user-events-page").then((m) => ({
    default: m.UserEventsPage,
  })),
);
const Dashboard = lazy(() =>
  import("~/screens/Admins/Dashboard/Dashboard").then((m) => ({
    default: m.Dashboard,
  })),
);
const ProviderDashboard = lazy(() =>
  import("~/screens/Admins/Providers/ProviderDashboard").then((m) => ({
    default: m.ProviderDashboard,
  })),
);

// Auth screens (non-critical-path)
const CodeValidation = lazy(() =>
  import("~/screens/Auth/CodeValidation/CodeValidation").then((m) => ({
    default: m.CodeValidation,
  })),
);
const EmailSent = lazy(() =>
  import("~/screens/Auth/EmailSent/EmailSent").then((m) => ({
    default: m.EmailSent,
  })),
);
const EmailVerified = lazy(() =>
  import("~/screens/Auth/EmailVerified/EmailVerified").then((m) => ({
    default: m.EmailVerified,
  })),
);
const IntakeForm = lazy(() =>
  import("~/screens/Auth/Signup/IntakeForm").then((m) => ({
    default: m.IntakeForm,
  })),
);
const MagicLink = lazy(() =>
  import("~/screens/Auth/MagicLink/MagicLink").then((m) => ({
    default: m.MagicLink,
  })),
);
const PendingVerification = lazy(() =>
  import("~/screens/Auth/PendingVerification/PendingVerification").then(
    (m) => ({ default: m.PendingVerification }),
  ),
);

// Hub screens
const BookVisit = lazy(() =>
  import("~/screens/Hub/BookVisit/BookVisit").then((m) => ({
    default: m.BookVisit,
  })),
);
const MeetClinician = lazy(() =>
  import("~/screens/Hub/BookVisit/views/MeetClinician").then((m) => ({
    default: m.MeetClinician,
  })),
);
const CoachPricing = lazy(() =>
  import("~/screens/Hub/BookVisit/views/MeetCoach/CoachPricing").then((m) => ({
    default: m.CoachPricing,
  })),
);
const MeetCoach = lazy(() =>
  import("~/screens/Hub/BookVisit/views/MeetCoach/MeetCoach").then((m) => ({
    default: m.MeetCoach,
  })),
);
const MoreInformation = lazy(() =>
  import("~/screens/Hub/BookVisit/views/MoreInformation").then((m) => ({
    default: m.MoreInformation,
  })),
);
const SchedulingProviderSelection = lazy(() =>
  import("~/screens/Hub/BookVisit/views/ProviderSelection/SchedulingProviderSelection").then(
    (m) => ({ default: m.SchedulingProviderSelection }),
  ),
);
const CancelVisitPage = lazy(() =>
  import("~/screens/Hub/CancelVisit/CancelVisitPage").then((m) => ({
    default: m.CancelVisitPage,
  })),
);
const ContactUs = lazy(() =>
  import("~/screens/Hub/ContactUs/ContactUs").then((m) => ({
    default: m.ContactUs,
  })),
);
const FAQ = lazy(() =>
  import("~/screens/Hub/FAQ/FAQ").then((m) => ({ default: m.FAQ })),
);
const Home = lazy(() =>
  import("~/screens/Hub/Home/Home").then((m) => ({ default: m.Home })),
);
const Resources = lazy(() =>
  import("~/screens/Hub/Resources/Resources").then((m) => ({
    default: m.Resources,
  })),
);

// Scheduling screens
const Demographics = lazy(() =>
  import("~/screens/Scheduling/Demographics/Demographics").then((m) => ({
    default: m.Demographics,
  })),
);
const InitialVisit = lazy(() =>
  import("~/screens/Scheduling/InitialVisit/InitialVisit").then((m) => ({
    default: m.InitialVisit,
  })),
);
const PersonalInformation = lazy(() =>
  import("~/screens/Scheduling/PersonalInformation/PersonalInformation").then(
    (m) => ({ default: m.PersonalInformation }),
  ),
);
const SchedulingConfirmedVisit = lazy(() =>
  import("~/screens/Scheduling/ConfirmedVisit/scheduling-visit-confirmed").then(
    (m) => ({ default: m.SchedulingConfirmedVisit }),
  ),
);
const SchedulingEmailInput = lazy(() =>
  import("~/screens/Scheduling/SchedulingEmailInput/scheduling-email-input").then(
    (m) => ({ default: m.SchedulingEmailInput }),
  ),
);

// Get-started / qualifier screens
const QualifierQuestions = lazy(() =>
  import("~/screens/get-started/qualifier-questions/qualifier-questions").then(
    (m) => ({ default: m.QualifierQuestions }),
  ),
);
const BookingStateInsurance = lazy(() =>
  import("~/screens/get-started/qualifier-questions/state-insurance/state-insurance").then(
    (m) => ({ default: m.StateInsurance }),
  ),
);
const JoinWaitlist = lazy(() =>
  import("~/screens/get-started/qualifier-questions/waitlist/join-waitlist").then(
    (m) => ({ default: m.JoinWaitlist }),
  ),
);
const JoinedWaitlist = lazy(() =>
  import("~/screens/get-started/qualifier-questions/waitlist/joined-waitlist").then(
    (m) => ({ default: m.JoinedWaitlist }),
  ),
);
const NonValidInsurance = lazy(() =>
  import("~/screens/get-started/qualifier-questions/waitlist/non-valid-insurance").then(
    (m) => ({ default: m.NonValidInsurance }),
  ),
);
const GetStartedEntry = lazy(() =>
  import("~/screens/get-started/qualifier-questions/entry/get-started-entry").then(
    (m) => ({ default: m.GetStartedEntry }),
  ),
);
const GoalScreen = lazy(() =>
  import("~/screens/get-started/qualifier-questions/goal/goal").then((m) => ({
    default: m.GoalScreen,
  })),
);
const OrientingInfoScreen = lazy(() =>
  import("~/screens/get-started/qualifier-questions/orienting-info/orienting-info").then(
    (m) => ({ default: m.OrientingInfoScreen }),
  ),
);

// Policies screens
const PatientAgreement = lazy(() =>
  import("~/screens/Policies/patient-agreement").then((m) => ({
    default: m.PatientAgreement,
  })),
);
const PrivacyPolicy = lazy(() =>
  import("~/screens/Policies/privacy-policy").then((m) => ({
    default: m.PrivacyPolicy,
  })),
);
const PrivacyPractices = lazy(() =>
  import("~/screens/Policies/privacy-practices").then((m) => ({
    default: m.PrivacyPractices,
  })),
);
const ConsumerHealthData = lazy(() =>
  import("~/screens/Policies/consumer-health-data").then((m) => ({
    default: m.ConsumerHealthData,
  })),
);
const TermsOfUse = lazy(() =>
  import("~/screens/Policies/terms-of-use").then((m) => ({
    default: m.TermsOfUse,
  })),
);

// User screens
const Profile = lazy(() =>
  import("~/screens/Users/Profile").then((m) => ({ default: m.Profile })),
);

const MaintenanceScreen = lazy(() =>
  import("~/screens/Maintenance/maintenance-screen").then((m) => ({
    default: m.MaintenanceScreen,
  })),
);

const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <SpinnerIcon />
  </div>
);

export const Router = () => {
  const location = useLocation();
  const { previousLocation } = (location.state ?? {}) as {
    previousLocation?: Location;
  };

  // Only trust `previousLocation` as a background if it's a real screen. When a
  // modal is opened from another modal that was itself cold-loaded (e.g. "See
  // Affected User Details" from a shared activity link), `useNavigateModal`
  // stores the current modal route as `previousLocation` - using it as the
  // background would match no top-level route and fall through to NotFound
  // behind the modal. Fall back to the parent-screen derivation in that case.
  const safePreviousLocation =
    previousLocation && !isModalRoute(previousLocation.pathname)
      ? previousLocation
      : undefined;

  // On a cold load (fresh tab / shared link) of a modal route, there's no
  // usable `previousLocation` to render behind the modal, so the background
  // Routes would otherwise match the real pathname and fall through to NotFound
  // - visible through the modal's backdrop-blur overlay. Render the modal's
  // parent screen instead.
  const backgroundLocation =
    safePreviousLocation ??
    (isModalRoute(location.pathname)
      ? { ...location, pathname: getModalParentRoute(location.pathname) }
      : location);

  return (
    <ChunkLoadErrorBoundary fallback={<ErrorBoundary />}>
      <div>
        <Suspense fallback={<PageLoader />}>
          <Routes location={backgroundLocation}>
            <Route path={ROUTES.home} element={<HomeRedirect />} />

            <Route element={<Guest />}>
              <Route path={ROUTES.login} element={<Login allowSignup />} />
              <Route
                path={ROUTES.fullLogin}
                element={<Login allowSignup={false} />}
              />

              <Route path={ROUTES.signup} element={<Signup />} />
              <Route
                path={ROUTES.signupBeanstalk}
                element={<Signup source={SOURCE.BEANSTALK} />}
              />
              <Route
                path={ROUTES.signupEmblem}
                element={<Signup source={SOURCE.EMBLEM_EMPLOYEES} />}
              />
              <Route
                path={ROUTES.signupEmblemILS}
                element={<Signup source={SOURCE.EMBLEM_ILS} />}
              />
              <Route
                path={ROUTES.signupMgbhp}
                element={<Signup source={SOURCE.MGBHP} />}
              />
              <Route
                path={ROUTES.signupQuickJoin}
                element={<Signup source={SOURCE.QUICKJOIN} />}
              />
              <Route
                path={ROUTES.signupActivityPartnership}
                element={<Signup source={SOURCE.ACTIVITY_PARTNERSHIP} />}
              />
              <Route path={ROUTES.intakeForm} element={<IntakeForm />} />

              <Route path={ROUTES.emailSent} element={<EmailSent />} />
              <Route
                path={ROUTES.codeValidation}
                element={<CodeValidation />}
              />
              <Route
                path={ROUTES.pendingVerification}
                element={<PendingVerification />}
              />
              <Route path={ROUTES.emailVerified} element={<EmailVerified />} />
              <Route path={ROUTES.magicLink} element={<MagicLink />} />

              {/* Scheduling */}
              <Route
                element={<Demographics />}
                path={ROUTES.schedulingDemographics}
              />
              <Route
                element={<InitialVisit />}
                path={ROUTES.schedulingInitialVisit}
              />
              <Route
                element={<SchedulingEmailInput />}
                path={ROUTES.schedulingEmailInput}
              />
              <Route
                element={<PersonalInformation />}
                path={ROUTES.schedulingPatientInformation}
              />

              <Route
                element={<SchedulingConfirmedVisit />}
                path={ROUTES.schedulingVisitConfirmed}
              />
            </Route>

            <Route path={ROUTES.getStarted} element={<QualifierQuestions />}>
              <Route index element={<GetStartedEntry />} />
              <Route
                path={ROUTES.getStartedInsurance}
                element={<BookingStateInsurance />}
              />
              <Route path={ROUTES.getStartedGoal} element={<GoalScreen />} />
              <Route
                path={ROUTES.getStartedOrientingInfo}
                element={<OrientingInfoScreen />}
              />
              <Route
                path={ROUTES.getStartedJoinWaitlist}
                element={<JoinWaitlist />}
              />
              <Route
                path={ROUTES.getStartedJoinedWaitlist}
                element={<JoinedWaitlist />}
              />
              <Route
                path={ROUTES.getStartedNonValidInsurance}
                element={<NonValidInsurance />}
              />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  only={[
                    ROLES.USER.value,
                    ROLES.ADMIN.value,
                    ROLES.SUPER_ADMIN.value,
                  ]}
                  redirectTo={ROUTES.dashboard}
                />
              }
            >
              <Route element={<MeetCoach />} path={ROUTES.hubCoach} />
              <Route element={<MeetCoach />} path={ROUTES.hubMeetCoach} />

              <Route element={<CoachPricing />} path={ROUTES.hubCoachPricing} />

              <Route
                element={<SchedulingProviderSelection />}
                path={ROUTES.hubMeetProviderSelection}
              />

              <Route
                element={<InitialVisit />}
                path={ROUTES.hubMeetProviderAppointments}
              />

              <Route
                element={<PersonalInformation />}
                path={ROUTES.hubMeetProviderPatientInformation}
              />
              <Route
                element={<SchedulingConfirmedVisit />}
                path={ROUTES.hubMeetProviderConfirmed}
              />

              <Route element={<HubLayout />}>
                <Route element={<Home />} path={ROUTES.hubHome} />

                <Route element={<BookVisit />} path={ROUTES.hubBookVisit} />

                <Route
                  element={<MeetClinician />}
                  path={ROUTES.hubMeetClinician}
                />
                <Route
                  element={<MoreInformation />}
                  path={ROUTES.hubMoreInformation}
                />
                <Route element={<Resources />} path={ROUTES.hubResources} />
                <Route element={<ContactUs />} path={ROUTES.hubContactUs} />
                <Route element={<FAQ />} path={ROUTES.hubFaq} />
                <Route
                  element={<CancelVisitPage />}
                  path={ROUTES.hubCancelVisit}
                />
              </Route>
              <Route element={<HubLayout />}>
                <Route element={<Profile />} path={ROUTES.hubProfile} />
              </Route>
            </Route>

            {/* ADMIN + STAFF ROUTES */}
            <Route
              element={
                <ProtectedRoute
                  only={[
                    ROLES.ADMIN.value,
                    ROLES.SUPER_ADMIN.value,
                    ROLES.STAFF.value,
                  ]}
                />
              }
            >
              <Route element={<Dashboard />} path={ROUTES.dashboard} />
              <Route
                element={<ProviderDashboard />}
                path={ROUTES.adminProviderDashboard}
              />
              <Route
                element={<BannerManagementPage />}
                path={ROUTES.adminBannerManagement}
              />
              <Route
                element={<InsuranceProvidersPage />}
                path={ROUTES.adminInsuranceProviders}
              />
              <Route
                element={<InsuranceProviderDetailPage />}
                path={ROUTES.adminInsuranceProviderDetail}
              />
              <Route
                element={<UserEventsPage />}
                path={ROUTES.adminUserEvents}
              />
            </Route>

            <Route path={ROUTES.maintenance} element={<MaintenanceScreen />} />

            {/* Consent Routes */}
            <Route path={ROUTES.privacyPolicy} element={<PrivacyPolicy />} />
            <Route
              path={ROUTES.patientAgreement}
              element={<PatientAgreement />}
            />
            <Route
              path={ROUTES.privacyPractices}
              element={<PrivacyPractices />}
            />
            <Route path={ROUTES.termsOfUse} element={<TermsOfUse />} />
            <Route
              path={ROUTES.consumerHealthData}
              element={<ConsumerHealthData />}
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        <Suspense fallback={null}>
          <Routes>
            <Route
              path="*"
              element={
                <ModalRouter
                  showModal={
                    !!previousLocation || isModalRoute(location.pathname)
                  }
                />
              }
            />
          </Routes>
        </Suspense>
      </div>
    </ChunkLoadErrorBoundary>
  );
};
